import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import localeJa from '@angular/common/locales/ja';
import localeNb from '@angular/common/locales/nb';
import localePl from '@angular/common/locales/pl';
import localeZh from '@angular/common/locales/zh';
import { inject, NgModule, Optional, provideAppInitializer, SkipSelf } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CookieService } from 'ngx-cookie-service';
import { NgxFloatUiModule, NgxFloatUiPlacements } from 'ngx-float-ui';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  BmdCookieService,
  ChartService,
  DashboardService,
  DialogService,
  GlobalAlertService,
  I18nService,
  IconsService,
  LoaderService,
  LocalesService,
  LoginRedirectService,
  NavbarService,
  PermissionService,
  PriceLimitsService,
  ProgramService,
  ResourceService,
  RouterService,
  SidenavService,
  SiteService,
  TimezonesService,
  TreeService,
} from '@service';
import { AuthHttpInterceptor, ErrorInterceptor, LoaderInterceptor, MockHttpInterceptor } from './interceptor';
import { CoreRoutingModule } from './routing';

registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeZh, 'zh-TW');
registerLocaleData(localePl, 'pl-PL');
registerLocaleData(localeJa, 'ja-JP');
registerLocaleData(localeNb, 'nb-NO');

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

const placement = NgxFloatUiPlacements.RIGHT;

@NgModule({
  exports: [CoreRoutingModule, TranslateModule],
  imports: [
    CoreRoutingModule,
    NgxFloatUiModule.forRoot({ placement }),
    TranslateModule.forRoot({
      loader: {
        deps: [HttpClient],
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
      },
    }),
  ],
  providers: [
    ChartService,
    BmdCookieService,
    DashboardService,
    DialogService,
    GlobalAlertService,
    I18nService,
    IconsService,
    LoaderService,
    LocalesService,
    LoginRedirectService,
    CookieService,
    ProgramService,
    ResourceService,
    RouterService,
    SidenavService,
    SiteService,
    TimezonesService,
    TreeService,
    PermissionService,
    PriceLimitsService,
    provideAppInitializer(() => {
      const iconService = inject(IconsService);
      return iconService.load();
    }),
    provideAppInitializer(async () => {
      const loginService = inject(LoginRedirectService);
      const permissionsService = inject(PermissionService);
      const navbarService = inject(NavbarService);
      await loginService.loadLoginPage();
      await navbarService.url();
      const permissions$ = permissionsService.getPermissions();
      return await firstValueFrom(permissions$);
    }),
    provideAppInitializer(() => {
      const routerService = inject(RouterService);
      return routerService.initRouterCheck();
    }),
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
    },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: MockHttpInterceptor },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: {
        strict: true,
        useUtc: false,
      },
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class CoreModule {
  // Make sure CoreModule is imported only by one NgModule the BMDModule
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import only in BMDModule');
    }
  }
}
